import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Switch } from "@/components/ui/switch";
import { Toaster, toast } from "sonner";
import {
    Users,
    FileText,
    Wand2,
    BarChart3,
    Shield,
    Cpu,
    Flag,
    UserCog,
    Trash2,
    Crown,
    Search,
    Sparkles,
    Check,
} from "lucide-react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "flags", label: "Feature Flags", icon: Flag },
    { id: "users", label: "Users", icon: Users },
    { id: "plans", label: "Plans", icon: Sparkles },
    { id: "ai", label: "AI Engine", icon: Cpu },
    { id: "profile", label: "Profile", icon: UserCog },
];

const TIER_COLORS = {
    FREE: "bg-aura-muted/15 text-aura-body border-aura-muted/30",
    PREMIUM: "bg-aura-violet/15 text-aura-violet border-aura-violet/40",
    PRO: "bg-aura-cyan/15 text-aura-cyan border-aura-cyan/40",
};

export default function Admin() {
    const [tab, setTab] = useState("overview");
    return (
        <Layout>
            <Toaster theme="light" position="top-center" />
            <div className="mb-6">
                <div className="font-jetbrains-mono text-xs tracking-[0.3em] text-aura-violet mb-2">
                    // ADMIN / CONTROL PANEL
                </div>
                <h1 className="font-syne text-4xl sm:text-5xl font-bold text-aura-text">Mission Control</h1>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-6 border-b border-aura-violet/10">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 -mb-px text-sm font-dm-sans transition ${
                            tab === t.id
                                ? "text-aura-violet border-b-2 border-aura-violet font-semibold"
                                : "text-aura-body hover:text-aura-text"
                        }`}
                        data-testid={`admin-tab-${t.id}`}
                    >
                        <t.icon className="w-4 h-4" />
                        {t.label}
                    </button>
                ))}
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={tab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                >
                    {tab === "overview" && <OverviewTab />}
                    {tab === "flags" && <FlagsTab />}
                    {tab === "users" && <UsersTab />}
                    {tab === "plans" && <PlansTab />}
                    {tab === "ai" && <AITab />}
                    {tab === "profile" && <ProfileTab />}
                </motion.div>
            </AnimatePresence>
        </Layout>
    );
}

/* ---------- Overview ---------- */
function OverviewTab() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/admin/stats").then(({ data }) => setStats(data));
    }, []);
    if (!stats)
        return <div className="glass p-10 text-center"><div className="cyber-spinner mx-auto" /></div>;
    const maxCount = Math.max(...stats.analyzeLast7Days.map((d) => d.count), 1);
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                    { l: "Total users", v: stats.totalUsers, i: Users },
                    { l: "Identity reports", v: stats.totalReports, i: FileText },
                    { l: "Bios generated", v: stats.totalBios, i: Wand2 },
                    { l: "FREE / PREM / PRO", v: `${stats.byTier.FREE}/${stats.byTier.PREMIUM}/${stats.byTier.PRO}`, i: BarChart3 },
                ].map((s) => (
                    <div key={s.l} className="glass p-5">
                        <s.i className="w-4 h-4 text-aura-violet mb-3" />
                        <div className="font-syne text-3xl font-bold text-aura-text">{s.v}</div>
                        <div className="font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-muted mt-1">{s.l}</div>
                    </div>
                ))}
            </div>
            <div className="glass p-6">
                <h3 className="font-syne text-lg font-semibold mb-4 text-aura-text">AI analyses — last 7 days</h3>
                <div className="flex items-end justify-between gap-2 h-40">
                    {stats.analyzeLast7Days.map((d) => (
                        <div key={d.date} className="flex-1 flex flex-col items-center gap-2">
                            <motion.div
                                initial={{ height: 0 }}
                                animate={{ height: `${(d.count / maxCount) * 100}%` }}
                                transition={{ type: "spring", stiffness: 70, damping: 14 }}
                                className="w-full max-w-[36px] rounded-t-md bg-gradient-to-t from-aura-violet to-aura-cyan"
                                style={{ boxShadow: "0 0 14px rgba(124,58,237,0.3)" }}
                            />
                            <div className="font-jetbrains-mono text-[10px] text-aura-muted">{d.date.slice(5)}</div>
                            <div className="font-jetbrains-mono text-[10px] text-aura-violet font-semibold">{d.count}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ---------- Flags ---------- */
function FlagsTab() {
    const [flags, setFlags] = useState([]);
    useEffect(() => {
        api.get("/admin/flags").then(({ data }) => setFlags(data));
    }, []);
    const toggle = async (key, enabled) => {
        try {
            await api.patch(`/admin/flags/${key}`, { enabled });
            setFlags((arr) => arr.map((f) => (f.key === key ? { ...f, enabled } : f)));
            toast.success(`${key} → ${enabled ? "ON" : "OFF"}`);
        } catch {
            toast.error("Update failed");
        }
    };
    return (
        <div className="glass p-6" data-testid="admin-flags-card">
            <h3 className="font-syne text-2xl font-semibold mb-4 text-aura-text">Feature Flags</h3>
            <div className="space-y-2">
                {flags.map((f) => (
                    <div key={f.key} className="flex items-center justify-between p-4 rounded-lg bg-white border border-aura-violet/10 hover:border-aura-violet/30 transition" data-testid={`flag-row-${f.key}`}>
                        <div className="flex-1 min-w-0 pr-4">
                            <div className="flex items-center gap-2 flex-wrap">
                                <code className="font-jetbrains-mono text-sm text-aura-violet font-semibold">{f.key}</code>
                                {(f.tiers || []).map((t) => (
                                    <span key={t} className="text-[9px] px-1.5 py-0.5 rounded font-jetbrains-mono uppercase tracking-wider bg-aura-violet/10 text-aura-violet border border-aura-violet/20">
                                        {t}
                                    </span>
                                ))}
                            </div>
                            <div className="text-xs text-aura-body mt-1">{f.description}</div>
                        </div>
                        <Switch checked={f.enabled} onCheckedChange={(v) => toggle(f.key, v)} data-testid={`flag-toggle-${f.key}`} />
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ---------- Users ---------- */
function UsersTab() {
    const { user: currentAdmin } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const load = async () => {
        const { data } = await api.get("/admin/users");
        setUsers(data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const updateTier = async (id, tier) => {
        try {
            const { data } = await api.patch(`/admin/users/${id}`, { tier });
            setUsers((arr) => arr.map((u) => (u.id === id ? data : u)));
            toast.success(`Plan updated to ${tier}`);
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || "Update failed");
        }
    };

    const toggleAdmin = async (id, isAdmin) => {
        try {
            const { data } = await api.patch(`/admin/users/${id}`, { isAdmin });
            setUsers((arr) => arr.map((u) => (u.id === id ? data : u)));
            toast.success(`Admin ${isAdmin ? "granted" : "revoked"}`);
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || "Update failed");
        }
    };

    const removeUser = async (u) => {
        if (!window.confirm(`Delete ${u.email}? This cascades all their data.`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            setUsers((arr) => arr.filter((x) => x.id !== u.id));
            toast.success("User deleted");
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || "Delete failed");
        }
    };

    const filtered = users.filter((u) =>
        !q || u.email.toLowerCase().includes(q.toLowerCase()) || u.name?.toLowerCase().includes(q.toLowerCase())
    );

    if (loading) return <div className="glass p-10 text-center"><div className="cyber-spinner mx-auto" /></div>;

    return (
        <div className="glass p-6" data-testid="admin-users-card">
            <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
                <h3 className="font-syne text-2xl font-semibold text-aura-text">Users <span className="text-aura-muted text-base font-dm-sans font-normal">· {users.length}</span></h3>
                <div className="relative">
                    <Search className="w-3.5 h-3.5 text-aura-muted absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search email or name…"
                        className="bg-aura-input border border-aura-violet/15 rounded-lg pl-9 pr-3 py-2 text-sm text-aura-text focus:border-aura-violet focus:outline-none w-64"
                        data-testid="users-search"
                    />
                </div>
            </div>
            <div className="overflow-x-auto -mx-2">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-aura-violet/10 text-left">
                            <th className="px-3 py-2 font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-muted">User</th>
                            <th className="px-3 py-2 font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-muted">Plan</th>
                            <th className="px-3 py-2 font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-muted">Activity</th>
                            <th className="px-3 py-2 font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-muted">Admin</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((u) => (
                            <tr key={u.id} className="border-b border-aura-violet/5 hover:bg-white/40 transition" data-testid={`user-row-${u.id}`}>
                                <td className="px-3 py-3">
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-aura-violet/30 to-aura-cyan/30 flex items-center justify-center font-syne text-xs font-bold text-aura-text">
                                            {u.name?.[0]?.toUpperCase() || "?"}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-dm-sans text-sm text-aura-text truncate">{u.name}</div>
                                            <div className="font-jetbrains-mono text-[10px] text-aura-muted truncate">{u.email}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-3 py-3">
                                    <select
                                        value={u.tier}
                                        onChange={(e) => updateTier(u.id, e.target.value)}
                                        className={`text-xs px-2 py-1 rounded border font-jetbrains-mono uppercase tracking-wider ${TIER_COLORS[u.tier]} cursor-pointer`}
                                        data-testid={`user-tier-${u.id}`}
                                    >
                                        <option value="FREE">FREE</option>
                                        <option value="PREMIUM">PREMIUM</option>
                                        <option value="PRO">PRO</option>
                                    </select>
                                </td>
                                <td className="px-3 py-3 text-xs text-aura-body whitespace-nowrap">
                                    <span className="font-jetbrains-mono">{u.reportsCount} reports · {u.biosCount} bios</span>
                                </td>
                                <td className="px-3 py-3">
                                    <Switch
                                        checked={!!u.isAdmin}
                                        onCheckedChange={(v) => toggleAdmin(u.id, v)}
                                        disabled={u.id === currentAdmin?.id}
                                        data-testid={`user-admin-${u.id}`}
                                    />
                                </td>
                                <td className="px-3 py-3 text-right">
                                    <button
                                        onClick={() => removeUser(u)}
                                        disabled={u.id === currentAdmin?.id}
                                        className="text-aura-muted hover:text-red-500 transition disabled:opacity-30 disabled:cursor-not-allowed"
                                        title={u.id === currentAdmin?.id ? "Cannot delete yourself" : "Delete user"}
                                        data-testid={`user-delete-${u.id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {filtered.length === 0 && <div className="text-center py-8 text-aura-muted text-sm">No users found.</div>}
        </div>
    );
}

/* ---------- AI Engine ---------- */
const AI_PROVIDERS = [
  { id: "openai",    name: "OpenAI",          icon: "?", color: "#10b981", keyPlaceholder: "sk-...",      docsUrl: "https://platform.openai.com/api-keys",          models: [{id:"gpt-4o",name:"GPT-4o",badge:"Recommended"},{id:"gpt-4o-mini",name:"GPT-4o mini",badge:"Fast & cheap"},{id:"gpt-4-turbo",name:"GPT-4 Turbo",badge:null}] },
  { id: "anthropic", name: "Anthropic Claude", icon: "?", color: "#f59e0b", keyPlaceholder: "sk-ant-...", docsUrl: "https://console.anthropic.com/settings/keys",    models: [{id:"claude-opus-4-5",name:"Claude Opus",badge:"Most powerful"},{id:"claude-sonnet-4-5",name:"Claude Sonnet",badge:"Balanced"},{id:"claude-haiku-4-5",name:"Claude Haiku",badge:"Fastest"}] },
  { id: "google",    name: "Google Gemini",    icon: "?", color: "#3b82f6", keyPlaceholder: "AIza...",    docsUrl: "https://aistudio.google.com/app/apikey",         models: [{id:"gemini-1.5-pro",name:"Gemini 1.5 Pro",badge:"Most capable"},{id:"gemini-1.5-flash",name:"Gemini 1.5 Flash",badge:"Faster"}] },
];

/* ---------- Plans ---------- */
function PlansTab() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);
    const [edits, setEdits] = useState({});

    useEffect(() => {
        api.get("/admin/plans").then(({ data }) => {
            setPlans(data);
            const init = {};
            data.forEach(p => { init[p.id] = { amount: p.amount, name: p.name, features: p.features.join("\n") }; });
            setEdits(init);
            setLoading(false);
        });
    }, []);

    const save = async (planId) => {
        setSaving(planId);
        try {
            const e = edits[planId];
            await api.patch(`/admin/plans/${planId}`, {
                amount: parseFloat(e.amount),
                name: e.name,
                features: e.features.split("\n").map(f => f.trim()).filter(Boolean),
            });
            toast.success(`${planId} updated`);
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || "Update failed");
        } finally {
            setSaving(null);
        }
    };

    if (loading) return <div className="glass p-10 text-center"><div className="cyber-spinner mx-auto" /></div>;

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {plans.map((p) => (
                    <div key={p.id} className="glass p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Crown className="w-4 h-4 text-aura-violet" />
                            <h3 className="font-syne text-xl font-semibold text-aura-text">{p.name}</h3>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-jetbrains-mono uppercase tracking-wider border ${TIER_COLORS[p.tier]}`}>{p.tier}</span>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Name</label>
                                <input
                                    value={edits[p.id]?.name || ""}
                                    onChange={e => setEdits(ed => ({ ...ed, [p.id]: { ...ed[p.id], name: e.target.value } }))}
                                    className="mt-1.5 w-full bg-aura-input border border-aura-violet/15 rounded-lg px-4 py-2.5 text-sm text-aura-text focus:border-aura-violet focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Price (�/month)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={edits[p.id]?.amount || ""}
                                    onChange={e => setEdits(ed => ({ ...ed, [p.id]: { ...ed[p.id], amount: e.target.value } }))}
                                    className="mt-1.5 w-full bg-aura-input border border-aura-violet/15 rounded-lg px-4 py-2.5 text-sm text-aura-text focus:border-aura-violet focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Features (1 per line)</label>
                                <textarea
                                    rows={6}
                                    value={edits[p.id]?.features || ""}
                                    onChange={e => setEdits(ed => ({ ...ed, [p.id]: { ...ed[p.id], features: e.target.value } }))}
                                    className="mt-1.5 w-full bg-aura-input border border-aura-violet/15 rounded-lg px-4 py-2.5 text-sm text-aura-text focus:border-aura-violet focus:outline-none resize-none font-dm-sans"
                                />
                            </div>
                            <button
                                onClick={() => save(p.id)}
                                disabled={saving === p.id}
                                className="w-full px-5 py-2.5 rounded-lg bg-gradient-to-r from-aura-violet to-aura-glow text-white text-sm font-dm-sans font-medium glow-violet-soft btn-ripple disabled:opacity-50"
                            >
                                {saving === p.id ? "Saving�" : "Save changes"}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function AITab() {
    const [config, setConfig] = useState({ provider: "openai", model: "gpt-4o", hasKeys: {} });
    const [keys, setKeys] = useState({ openai: "", anthropic: "", google: "" });
    const [showKey, setShowKey] = useState({ openai: false, anthropic: false, google: false });
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchConfig = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/ai-config");
            setConfig(data);
        } catch (e) {
            toast.error("Failed to load AI config");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchConfig(); }, [fetchConfig]);

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/admin/ai-config", { provider: config.provider, model: config.model, keys });
            setKeys({ openai: "", anthropic: "", google: "" });
            await fetchConfig();
            toast.success("AI configuration saved");
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const activeProvider = AI_PROVIDERS.find(p => p.id === config.provider) || AI_PROVIDERS[0];

    if (loading) return <div className="glass p-10 text-center"><div className="cyber-spinner mx-auto" /></div>;

    return (
        <div className="space-y-6" data-testid="admin-ai-card">
            <div className="glass p-6">
                <div className="flex items-start gap-3 mb-6">
                    <Cpu className="w-5 h-5 text-aura-violet mt-0.5" />
                    <div>
                        <h3 className="font-syne text-2xl font-semibold text-aura-text">AI Engine</h3>
                        <p className="text-sm text-aura-body mt-1">Your own API keys. No intermediary, no balance to top up.</p>
                    </div>
                </div>

                {/* Active summary */}
                <div className="mb-6 p-3 rounded-lg bg-aura-violet/5 border border-aura-violet/20 flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-green-400 shadow-[0_0_6px_#4ade80] flex-shrink-0" />
                    <span className="text-xs text-aura-body font-jetbrains-mono">
                        Active: <strong className="text-aura-text">{config.provider}/{config.model}</strong>
                        {config.hasKeys?.[config.provider]
                            ? <span className="ml-2 text-green-400">� Key configured ?</span>
                            : <span className="ml-2 text-red-400">� No key � add one below</span>}
                    </span>
                </div>

                {/* Provider */}
                <div className="mb-6">
                    <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Provider</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                        {AI_PROVIDERS.map(p => (
                            <button key={p.id} onClick={() => setConfig(c => ({ ...c, provider: p.id, model: p.models[0].id }))}
                                className={`text-left p-4 rounded-xl border transition ${config.provider === p.id ? "border-aura-violet bg-aura-violet/5 glow-violet-soft" : "border-aura-violet/15 hover:border-aura-violet/40"}`}>
                                <div className="flex items-center justify-between">
                                    <span className="font-syne text-base font-semibold text-aura-text">{p.name}</span>
                                    {config.provider === p.id && <Check className="w-4 h-4 text-aura-violet" strokeWidth={2.5} />}
                                </div>
                                <div className="flex items-center gap-1.5 mt-1">
                                    <span className={`w-1.5 h-1.5 rounded-full ${config.hasKeys?.[p.id] ? "bg-green-400" : "bg-aura-muted"}`} />
                                    <span className="text-xs text-aura-muted">{config.hasKeys?.[p.id] ? "Key configured" : "No key"}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Model */}
                <div className="mb-6">
                    <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Model � {activeProvider.name}</label>
                    <div className="space-y-2 mt-2">
                        {activeProvider.models.map(m => (
                            <button key={m.id} onClick={() => setConfig(c => ({ ...c, model: m.id }))}
                                className={`w-full text-left p-3 rounded-lg border transition flex items-center justify-between ${config.model === m.id ? "border-aura-violet bg-aura-violet/5" : "border-aura-violet/15 hover:border-aura-violet/40"}`}>
                                <div>
                                    <div className="font-dm-sans text-sm font-medium text-aura-text">{m.name}</div>
                                    <div className="font-jetbrains-mono text-[10px] text-aura-muted mt-0.5">{m.id}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {m.badge && <span className="text-[10px] font-syne font-semibold px-2 py-0.5 rounded-full bg-aura-violet/10 text-aura-violet border border-aura-violet/20">{m.badge}</span>}
                                    {config.model === m.id && <Check className="w-4 h-4 text-aura-violet" strokeWidth={2.5} />}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* API Keys */}
                <div className="mb-6">
                    <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">API Keys</label>
                    <div className="space-y-3 mt-2">
                        {AI_PROVIDERS.map(p => (
                            <div key={p.id}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs text-aura-body flex items-center gap-1.5">
                                        <span style={{color: p.color}}>{p.icon}</span> {p.name}
                                        {config.hasKeys?.[p.id] && <span className="text-green-400 text-[10px]">� Saved ?</span>}
                                    </span>
                                    <a href={p.docsUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-aura-violet hover:text-aura-glow">Get key ?</a>
                                </div>
                                <div className="relative">
                                    <input
                                        type={showKey[p.id] ? "text" : "password"}
                                        placeholder={config.hasKeys?.[p.id] ? "Leave empty to keep existing key" : p.keyPlaceholder}
                                        value={keys[p.id]}
                                        onChange={e => setKeys(k => ({ ...k, [p.id]: e.target.value }))}
                                        className="w-full bg-black/40 border border-aura-violet/20 rounded-lg px-3 py-2.5 pr-10 text-aura-text font-jetbrains-mono text-xs outline-none focus:border-aura-violet/50 placeholder:text-aura-muted"
                                    />
                                    <button type="button" onClick={() => setShowKey(s => ({ ...s, [p.id]: !s[p.id] }))}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-aura-muted hover:text-aura-body text-sm">
                                        {showKey[p.id] ? "??" : "??"}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Save */}
                <div className="flex items-center justify-end gap-4">
                    <button onClick={save} disabled={saving}
                        className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-aura-violet to-aura-glow text-white text-sm font-dm-sans font-medium glow-violet-soft btn-ripple disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="ai-config-save">
                        {saving ? "Saving�" : "Save configuration"}
                    </button>
                </div>
            </div>
        </div>
    );
}
/* ---------- Profile (admin self-edit) ---------- */
function ProfileTab() {
    const { user, refresh } = useAuth();
    const [name, setName] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [saving, setSaving] = useState(false);

    const saveBasics = async () => {
        if (name === user?.name && email === user?.email) return toast.info("Nothing to save");
        setSaving(true);
        try {
            await api.patch("/admin/me", { name, email });
            await refresh();
            toast.success("Profile updated");
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    const savePassword = async () => {
        if (!currentPassword || !newPassword) return toast.error("Fill both passwords");
        if (newPassword.length < 8) return toast.error("New password must be ≥ 8 chars");
        setSaving(true);
        try {
            await api.patch("/admin/me", { currentPassword, newPassword });
            setCurrentPassword("");
            setNewPassword("");
            toast.success("Password changed");
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6 max-w-2xl" data-testid="admin-profile-card">
            <div className="glass p-6">
                <div className="flex items-center gap-2 mb-4">
                    <UserCog className="w-5 h-5 text-aura-violet" />
                    <h3 className="font-syne text-xl font-semibold text-aura-text">Identity</h3>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Display name</label>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="mt-1.5 w-full bg-aura-input border border-aura-violet/15 rounded-lg px-4 py-3 text-sm text-aura-text focus:border-aura-violet focus:outline-none"
                            data-testid="profile-name-input"
                        />
                    </div>
                    <div>
                        <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Email</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-1.5 w-full bg-aura-input border border-aura-violet/15 rounded-lg px-4 py-3 text-sm text-aura-text focus:border-aura-violet focus:outline-none"
                            data-testid="profile-email-input"
                        />
                    </div>
                    <button
                        onClick={saveBasics}
                        disabled={saving}
                        className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-aura-violet to-aura-glow text-white text-sm font-dm-sans font-medium glow-violet-soft btn-ripple disabled:opacity-60"
                        data-testid="profile-save-btn"
                    >
                        Save profile
                    </button>
                </div>
            </div>

            <div className="glass p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5 text-aura-violet" />
                    <h3 className="font-syne text-xl font-semibold text-aura-text">Change password</h3>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">Current password</label>
                        <input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="mt-1.5 w-full bg-aura-input border border-aura-violet/15 rounded-lg px-4 py-3 text-sm text-aura-text focus:border-aura-violet focus:outline-none"
                            data-testid="profile-currentpw-input"
                        />
                    </div>
                    <div>
                        <label className="font-jetbrains-mono text-[10px] uppercase tracking-[0.2em] text-aura-muted">New password (min 8 chars)</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="mt-1.5 w-full bg-aura-input border border-aura-violet/15 rounded-lg px-4 py-3 text-sm text-aura-text focus:border-aura-violet focus:outline-none"
                            data-testid="profile-newpw-input"
                        />
                    </div>
                    <button
                        onClick={savePassword}
                        disabled={saving}
                        className="px-5 py-2.5 rounded-lg border border-aura-violet/30 text-aura-violet hover:bg-aura-violet/5 text-sm font-dm-sans font-medium btn-ripple disabled:opacity-60"
                        data-testid="profile-changepw-btn"
                    >
                        Change password
                    </button>
                </div>
            </div>

            <div className="glass p-6">
                <div className="flex items-center gap-2 mb-3">
                    <Crown className="w-4 h-4 text-aura-violet" />
                    <span className="font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-violet font-semibold">{user?.tier} TIER · ADMIN</span>
                </div>
                <div className="text-xs text-aura-muted">User ID: <code className="font-jetbrains-mono">{user?.id}</code></div>
            </div>
        </div>
    );
}



